/**
 * Duplicate Sales Exception Config (per-group view)
 *
 * Covers: ex-sale-ls-duplicate (shown to users as "Duplicate Sales")
 *
 * Mirrors `duplicateExceptions.ts` (products) so the UI pattern stays
 * consistent: one row per detector group, a CountChip previewing the
 * member invoices, and a "Compare Duplicates" button (or row click)
 * opening the side-by-side comparison in `DuplicateGroupCompareDialog`,
 * powered by the group-detail endpoint.
 */
import React from 'react'
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material'
import { withTooltip } from '../shared/muiElements'
import {
  formatCurrency,
  formatBusinessDate,
} from '../../../utils/inspectorFormatters'
import CountChip, { CountChipItem } from '../../../components/inspector/shared/CountChip'
import inspectorApi from '../../../services/inspector/api'
import { NUMERIC_FONT_SX } from '../../../theme/numericFont'
import { LightspeedIcon, XeroIcon } from '../../../components/SystemIcons'
import {
  handleLightspeedLinkClick,
  handleXeroLinkClick,
} from '../../../utils/externalLinks'
import type { ExceptionConfig, ExceptionColumnDef } from '../types'
import { composeRowSubtitle, textPart } from '../shared/rowSubtitle'

interface GroupMember {
  ls_id?: string
  invoice_number?: string | null
  sale_date?: string | null
  client_name?: string | null
  outlet_name?: string | null
  total_amount_incl_tax?: number | null
  status?: string | null
  score?: number | null
  lightspeed_link?: string | null
  xero_link?: string | null
}

const scoreColor = (
  score: number | null | undefined,
): 'warning' | 'info' | 'default' => {
  if (score == null) return 'default'
  if (score >= 85) return 'warning'
  return 'info'
}

const renderMatchScore = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const score = typeof row.score === 'number' ? row.score : null
  const label = score !== null ? `${score}%` : '-'
  return React.createElement(Chip, {
    size: 'small',
    variant: 'filled',
    label,
    color: scoreColor(score),
    sx: { ...NUMERIC_FONT_SX, fontWeight: 700, minWidth: 56 },
  })
}

const renderInvoiceNumber = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const rep: string = row.representative_invoice || '-'
  return React.createElement(
    Typography,
    {
      variant: 'body2',
      sx: {
        fontWeight: 500,
        fontFamily: 'monospace',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: 200,
      },
      title: row.invoice_numbers_display || rep,
    },
    rep,
  )
}

const renderInvoiceCount = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const members: GroupMember[] = Array.isArray(row.members) ? row.members : []
  const items: CountChipItem[] = members.map((m, idx) => ({
    id: m.ls_id || `${row.group_id}-${idx}`,
    primary: m.invoice_number || m.ls_id?.slice(0, 8) || '-',
    secondary: m.client_name || null,
    metrics: [
      typeof m.score === 'number' ? `${m.score}%` : '-',
      m.sale_date ? formatBusinessDate(m.sale_date) : '-',
      m.total_amount_incl_tax != null
        ? formatCurrency(m.total_amount_incl_tax)
        : '-',
    ],
    link: m.lightspeed_link || undefined,
  }))
  return React.createElement(CountChip, {
    count: row.member_count || members.length,
    items,
    label: 'Invoices in group',
    metricHeaders: ['Score', 'Sale date', 'Amount'],
  })
}

const renderMatchReasons = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const reasons: string[] = Array.isArray(row.match_reasons)
    ? row.match_reasons
    : []
  if (!reasons.length) return '-'
  const chips = reasons.map((reason, i) =>
    React.createElement(Chip, {
      key: `${reason}-${i}`,
      size: 'small',
      variant: 'outlined',
      label: reason.replace(/_/g, ' '),
      sx: { height: 20, fontSize: 11 },
    }),
  )
  return React.createElement(
    Box,
    { sx: { display: 'flex', flexWrap: 'wrap', gap: 0.5 } },
    ...chips,
  )
}

const renderClient = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const clients: string[] = Array.isArray(row.client_names) ? row.client_names : []
  if (!clients.length) return '-'
  const primary = clients[0]
  const extra = clients.length - 1
  const tooltipNode =
    extra > 0
      ? React.createElement(
          Box,
          { sx: { p: 0.5 } },
          React.createElement(
            Typography,
            {
              variant: 'caption',
              sx: { fontWeight: 600, display: 'block', mb: 0.5 },
            },
            'Clients in group',
          ),
          ...clients.map((c) =>
            React.createElement(
              Typography,
              { key: c, variant: 'caption', sx: { display: 'block' } },
              c,
            ),
          ),
        )
      : primary
  return withTooltip(
    { title: tooltipNode, arrow: true, placement: 'top' },
    React.createElement(
      Stack,
      {
        direction: 'row',
        alignItems: 'center',
        spacing: 0.5,
        sx: { cursor: extra > 0 ? 'help' : 'default' },
      },
      React.createElement(
        Typography,
        {
          variant: 'body2',
          sx: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 180,
          },
        },
        primary,
      ),
      extra > 0 &&
        React.createElement(Chip, {
          size: 'small',
          variant: 'outlined',
          label: `+${extra}`,
          sx: { height: 18, fontSize: 10, fontWeight: 600 },
        }),
    ),
  )
}

const renderSaleDateRange = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const oldest: string | null = row.oldest_sale_date || null
  const newest: string | null = row.newest_sale_date || null
  if (!oldest && !newest) return '-'
  if (oldest && newest && oldest !== newest) {
    return `${formatBusinessDate(oldest)} – ${formatBusinessDate(newest)}`
  }
  return formatBusinessDate(oldest || newest || '')
}

const renderOutlet = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const outlets: string[] = Array.isArray(row.outlet_names) ? row.outlet_names : []
  if (!outlets.length) return '-'
  const primary = outlets[0]
  const extra = outlets.length - 1
  return React.createElement(
    Stack,
    { direction: 'row', alignItems: 'center', spacing: 0.5 },
    React.createElement(
      Typography,
      {
        variant: 'body2',
        sx: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          maxWidth: 180,
        },
        title: outlets.join(', '),
      },
      primary,
    ),
    extra > 0 &&
      React.createElement(Chip, {
        size: 'small',
        variant: 'outlined',
        label: `+${extra}`,
        sx: { height: 18, fontSize: 10, fontWeight: 600 },
      }),
  )
}

// Compact "jump to first member in LS/Xero" cell. Uses the canonical
// system icons from SystemIcons.tsx rather than hand-rolled buttons.
const renderSystemLinks = (
  _value: any,
  row: Record<string, any>,
): React.ReactNode => {
  const members: GroupMember[] = Array.isArray(row.members) ? row.members : []
  const first = members[0]
  if (!first) return null
  const label = first.invoice_number || first.ls_id?.slice(0, 8) || 'sale'

  const iconButton = (
    link: string,
    onClick: (e: React.MouseEvent<HTMLAnchorElement>) => void,
    Icon: React.FC<{ size?: number }>,
    title: string,
  ) =>
    React.createElement(
      // Box renders as an <a>; createElement infers the div variant, so
      // href/onClick (anchor-typed) need the component cast to type-check.
      Box as any,
      {
        key: title,
        component: 'a',
        href: link,
        onClick,
        target: '_blank',
        rel: 'noopener noreferrer',
        title,
        sx: {
          display: 'inline-flex',
          alignItems: 'center',
          p: 0.25,
          borderRadius: 0.5,
          cursor: 'pointer',
          transition: 'background-color 120ms',
          '&:hover': { backgroundColor: 'action.hover' },
        },
      },
      React.createElement(Icon, { size: 18 }),
    )

  const icons: React.ReactNode[] = []
  if (first.lightspeed_link) {
    icons.push(
      iconButton(
        first.lightspeed_link,
        (e) =>
          handleLightspeedLinkClick(e, first.lightspeed_link!, `Sale ${label}`),
        LightspeedIcon,
        'Open in Lightspeed',
      ),
    )
  }
  if (first.xero_link) {
    icons.push(
      iconButton(
        first.xero_link,
        (e) => handleXeroLinkClick(e, first.xero_link!, `Invoice ${label}`),
        XeroIcon,
        'Open in Xero',
      ),
    )
  }
  if (!icons.length) return '-'
  return React.createElement(
    Stack,
    { direction: 'row', spacing: 0.5, alignItems: 'center' },
    ...icons,
  )
}

const inspectorColumns: ExceptionColumnDef[] = [
  {
    key: 'score',
    label: 'Match',
    align: 'center',
    contexts: ['inspector'],
    render: renderMatchScore,
    getSortValue: (row) => Number(row.score) || 0,
  },
  {
    key: 'representative_invoice',
    label: 'Invoice #',
    align: 'left',
    minWidth: 180,
    contexts: ['inspector'],
    render: renderInvoiceNumber,
    getSortValue: (row) => row.representative_invoice || '',
  },
  {
    key: 'member_count',
    label: '# Invoices',
    align: 'center',
    type: 'integer',
    contexts: ['inspector'],
    render: renderInvoiceCount,
    getSortValue: (row) => Number(row.member_count) || 0,
    exportFormat: 'text',
  },
  {
    key: 'client_names',
    label: 'Client',
    align: 'left',
    contexts: ['inspector'],
    render: renderClient,
    getSortValue: (row) =>
      Array.isArray(row.client_names) ? row.client_names[0] || '' : '',
  },
  {
    key: 'match_reasons',
    label: 'Match reasons',
    contexts: ['inspector'],
    render: renderMatchReasons,
    getSortValue: (row) =>
      Array.isArray(row.match_reasons) ? row.match_reasons.join(',') : '',
  },
  {
    key: 'total_amount',
    label: 'Total',
    align: 'right',
    type: 'currency',
    contexts: ['inspector'],
    getSortValue: (row) => Number(row.total_amount) || 0,
    exportFormat: 'currency',
  },
  {
    key: 'oldest_sale_date',
    label: 'Sale date',
    contexts: ['inspector'],
    render: renderSaleDateRange,
    getSortValue: (row) => row.newest_sale_date || '',
    exportFormat: 'date',
  },
  {
    key: 'outlet_names',
    label: 'Outlet',
    contexts: ['inspector'],
    render: renderOutlet,
    getSortValue: (row) =>
      Array.isArray(row.outlet_names) ? row.outlet_names.join(',') : '',
  },
  {
    key: 'systems',
    label: 'Open',
    align: 'center',
    contexts: ['inspector'],
    render: renderSystemLinks,
  },
]

export const duplicateSaleConfig: ExceptionConfig = {
  codes: ['ex-sale-ls-duplicate'],
  columns: inspectorColumns,
  // The flat sales-exceptions route serves one row per sale; the canonical
  // duplicate view is one row per detector group, served by the
  // duplicate-sales tools endpoint - override the route's domain fetch.
  fetchOverride: (fpCode, params) =>
    inspectorApi.getDuplicateSaleGroups(fpCode, params),
  defaultSortColumn: 'score',
  defaultSortDirection: 'desc',
  complexity: 'comparison',
  rowKeyField: 'row_key',
  getRowSubtitle: row =>
    composeRowSubtitle([
      typeof row.member_count === 'number' && row.member_count > 0
        ? `${row.member_count} sales in group`
        : null,
      row.score != null ? `score ${row.score}` : null,
      textPart(row.customer_name),
    ]),
  // Side-by-side group comparison lives in DuplicateGroupCompareDialog -
  // opened by the per-row "Compare Duplicates" button or a row click
  // (replaces the old inline expandable rendering).
  rowDetail: {
    type: 'duplicate-group',
    headerLabel: 'Actions',
    groupKind: 'sale',
    getGroupId: (row) => row.group_id ?? null,
    getDialogTitle: (row) => row.representative_invoice || undefined,
    rowClick: true,
  },
}
